import { useState, useEffect } from 'react'
import { useForm, Controller, useFieldArray } from 'react-hook-form'
import { useParams, useNavigate } from 'react-router'
import { Plus, Trash2, Clock, ArrowLeft } from 'lucide-react'
import { toast } from 'sonner'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Checkbox } from '@/components/ui/checkbox'
import { Skeleton } from '@/components/ui/skeleton'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { useApiClient } from '@/shared/api'
import { useShowDialog } from '@/hooks/useShowDialog'

type DayOfWeek = 'Monday' | 'Tuesday' | 'Wednesday' | 'Thursday' | 'Friday' | 'Saturday' | 'Sunday'

interface TimePeriod {
  startTime: string
  endTime: string
}

interface ScheduleBlock {
  daysOfWeek: DayOfWeek[]
  periods: TimePeriod[]
}

interface DialerSettings {
  campaign: {
    id: number
    name: string
    config: {
      maxActiveConferences: number
      maxSimultaneousCalls: number
      ratio: number
      maxDialPerSecond: number
      maxCalls: number
    }
    customerLimits: {
      maxActiveConferences: number
      maxSimultaneousCalls: number
      ratio: number
      maxDialPerSecond: number
    }
    schedule: {
      schedules: ScheduleBlock[]
    }
  }
}

interface FormValues {
  maxActiveConferences: number
  maxSimultaneousCalls: number
  ratio: number
  maxDialPerSecond: number
  maxCalls: number
  schedules: ScheduleBlock[]
}

const DAYS_OF_WEEK: DayOfWeek[] = [
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
  'Sunday',
]

export function DialerSettings() {
  const { campaignId: campaignIdParam } = useParams<{ campaignId: string }>()
  const campaignId = parseInt(campaignIdParam || '0')
  const apiClient = useApiClient()
  const navigate = useNavigate()

  const [settings, setSettings] = useState<DialerSettings | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [isSaving, setIsSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Fetch settings
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const data = await apiClient<DialerSettings>(`/dialer/campaign/settings?campaign_id=${campaignId}`)
        setSettings(data)
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load settings')
      } finally {
        setIsLoading(false)
      }
    }

    if (campaignId) {
      fetchSettings()
    }
  }, [campaignId, apiClient])

  const {
    register,
    handleSubmit,
    control,
    formState: { errors, isDirty },
    reset,
  } = useForm<FormValues>({
    defaultValues: {
      maxActiveConferences: 0,
      maxSimultaneousCalls: 0,
      ratio: 0,
      maxDialPerSecond: 0,
      maxCalls: 0,
      schedules: [],
    },
  })

  useShowDialog(isDirty)

  // Reset form when settings load
  useEffect(() => {
    if (settings) {
      reset({
        maxActiveConferences: settings.campaign.config.maxActiveConferences,
        maxSimultaneousCalls: settings.campaign.config.maxSimultaneousCalls,
        ratio: settings.campaign.config.ratio,
        maxDialPerSecond: settings.campaign.config.maxDialPerSecond,
        maxCalls: settings.campaign.config.maxCalls,
        schedules: settings.campaign.schedule.schedules,
      })
    }
  }, [settings, reset])

  const {
    fields: scheduleFields,
    append: appendSchedule,
    remove: removeSchedule,
  } = useFieldArray({
    control,
    name: 'schedules',
  })

  const onSubmit = async (values: FormValues) => {
    setIsSaving(true)

    try {
      await apiClient('/dialer/campaign/config', {
        method: 'PUT',
        body: JSON.stringify({
          campaignId,
          maxActiveConferences: values.maxActiveConferences,
          maxSimultaneousCalls: values.maxSimultaneousCalls,
          ratio: values.ratio,
          maxDialPerSecond: values.maxDialPerSecond,
          maxCalls: values.maxCalls,
          schedule: {
            schedules: values.schedules,
          },
        }),
      })

      toast.success('Settings saved successfully!')
      setTimeout(() => {
        navigate('/dialer')
      }, 1500)
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to save settings')
    } finally {
      setIsSaving(false)
    }
  }

  if (isLoading) {
    return (
      <div className="w-full h-full p-4 overflow-y-auto">
        <div className="max-w-4xl mx-auto space-y-4">
          <Skeleton className="h-8 w-48" />
          <Skeleton className="h-64 w-full" />
          <Skeleton className="h-64 w-full" />
        </div>
      </div>
    )
  }

  if (error || !settings) {
    return (
      <div className="w-full h-full p-4 overflow-y-auto">
        <div className="max-w-4xl mx-auto">
          <Alert variant="destructive">
            <AlertTitle>Error</AlertTitle>
            <AlertDescription>{error || 'Failed to load campaign settings'}</AlertDescription>
          </Alert>
        </div>
      </div>
    )
  }

  const { campaign } = settings

  return (
    <div className="w-full h-full p-4 overflow-y-auto">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-4 flex items-center gap-4">
          <Button variant="outline" size="sm" onClick={() => navigate('/dialer')}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
          <div>
            <h1 className="text-heading font-bold text-gray-900 mb-1">Campaign Settings</h1>
            <p className="text-detail text-gray-600">Campaign: {campaign.name}</p>
          </div>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          {/* Dialer Configuration */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-body">Dialer Configuration</CardTitle>
              <CardDescription>
                Configure the dialer behavior and limits for this campaign
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="maxActiveConferences">
                    Active Conferences
                  </Label>
                  <Input
                    {...register('maxActiveConferences', {
                      valueAsNumber: true,
                      min: { value: 1, message: 'Must be at least 1' },
                      max: {
                        value: campaign.customerLimits.maxActiveConferences,
                        message: `Must be at most ${campaign.customerLimits.maxActiveConferences}`,
                      },
                    })}
                    id="maxActiveConferences"
                    type="number"
                    min="1"
                    max={campaign.customerLimits.maxActiveConferences}
                    className={errors.maxActiveConferences ? 'border-red-500' : ''}
                  />
                  <p className="text-detail text-gray-500">
                    Maximum: {campaign.customerLimits.maxActiveConferences}
                  </p>
                  {errors.maxActiveConferences && (
                    <p className="text-detail text-red-500">{errors.maxActiveConferences.message}</p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="maxSimultaneousCalls">
                    Max Simultaneous Calls
                  </Label>
                  <Input
                    {...register('maxSimultaneousCalls', {
                      valueAsNumber: true,
                      min: { value: 1, message: 'Must be at least 1' },
                      max: {
                        value: campaign.customerLimits.maxSimultaneousCalls,
                        message: `Must be at most ${campaign.customerLimits.maxSimultaneousCalls}`,
                      },
                    })}
                    id="maxSimultaneousCalls"
                    type="number"
                    min="1"
                    max={campaign.customerLimits.maxSimultaneousCalls}
                    className={errors.maxSimultaneousCalls ? 'border-red-500' : ''}
                  />
                  <p className="text-detail text-gray-500">
                    Maximum: {campaign.customerLimits.maxSimultaneousCalls}
                  </p>
                  {errors.maxSimultaneousCalls && (
                    <p className="text-detail text-red-500">{errors.maxSimultaneousCalls.message}</p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="ratio">
                    Ratio
                  </Label>
                  <Input
                    {...register('ratio', {
                      valueAsNumber: true,
                      min: { value: 1, message: 'Must be at least 1' },
                      max: {
                        value: campaign.customerLimits.ratio,
                        message: `Must be at most ${campaign.customerLimits.ratio}`,
                      },
                    })}
                    id="ratio"
                    type="number"
                    min="1"
                    max={campaign.customerLimits.ratio}
                    step="1"
                    className={errors.ratio ? 'border-red-500' : ''}
                  />
                  <p className="text-detail text-gray-500">Maximum: {campaign.customerLimits.ratio}</p>
                  {errors.ratio && <p className="text-detail text-red-500">{errors.ratio.message}</p>}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="maxDialPerSecond">
                    Max Dial Per Second
                  </Label>
                  <Input
                    {...register('maxDialPerSecond', {
                      valueAsNumber: true,
                      min: { value: 1, message: 'Must be at least 1' },
                      max: {
                        value: campaign.customerLimits.maxDialPerSecond,
                        message: `Must be at most ${campaign.customerLimits.maxDialPerSecond}`,
                      },
                    })}
                    id="maxDialPerSecond"
                    type="number"
                    min="1"
                    max={campaign.customerLimits.maxDialPerSecond}
                    className={errors.maxDialPerSecond ? 'border-red-500' : ''}
                  />
                  <p className="text-detail text-gray-500">
                    Maximum: {campaign.customerLimits.maxDialPerSecond}
                  </p>
                  {errors.maxDialPerSecond && (
                    <p className="text-detail text-red-500">{errors.maxDialPerSecond.message}</p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="maxCalls">
                    Max Calls Per Day
                  </Label>
                  <Input
                    {...register('maxCalls', {
                      valueAsNumber: true,
                      min: { value: 0, message: 'Must be 0 or greater' },
                    })}
                    id="maxCalls"
                    type="number"
                    min="0"
                    className={errors.maxCalls ? 'border-red-500' : ''}
                  />
                  <p className="text-detail text-gray-500">0 = Unlimited</p>
                  {errors.maxCalls && (
                    <p className="text-detail text-red-500">{errors.maxCalls.message}</p>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Schedule Configuration */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-body flex items-center gap-2">
                <Clock className="h-4 w-4" />
                Calling Schedule
              </CardTitle>
              <CardDescription>
                Define when the dialer is allowed to make calls. You can create multiple schedule
                blocks for different days and time periods.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {scheduleFields.length === 0 ? (
                <div className="text-center py-6 text-gray-500">
                  <Clock className="h-8 w-8 mx-auto mb-2 text-gray-400" />
                  <p className="text-body mb-1">No schedule configured</p>
                  <p className="text-detail mb-4">Add a schedule block to define calling hours</p>
                </div>
              ) : (
                scheduleFields.map((scheduleField, scheduleIndex) => (
                  <div
                    key={scheduleField.id}
                    className="border rounded-lg p-3 space-y-3 bg-gray-50"
                  >
                    <div className="flex items-center justify-between">
                      <h4 className="text-body font-semibold text-gray-700">
                        Schedule Block {scheduleIndex + 1}
                      </h4>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => removeSchedule(scheduleIndex)}
                        className="h-8 text-red-600 hover:text-red-700 hover:bg-red-50"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>

                    {/* Days of Week Selection */}
                    <div className="space-y-2">
                      <Label>Days of Week</Label>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                        {DAYS_OF_WEEK.map((day) => (
                          <Controller
                            key={day}
                            name={`schedules.${scheduleIndex}.daysOfWeek`}
                            control={control}
                            render={({ field }) => {
                              const isChecked = field.value?.includes(day) || false
                              return (
                                <div className="flex items-center space-x-2 border rounded-md p-2 hover:bg-white transition-colors">
                                  <Checkbox
                                    id={`schedule-${scheduleIndex}-${day}`}
                                    checked={isChecked}
                                    onCheckedChange={(checked) => {
                                      const currentValue = field.value || []
                                      if (checked) {
                                        field.onChange([...currentValue, day])
                                      } else {
                                        field.onChange(
                                          currentValue.filter((d: DayOfWeek) => d !== day)
                                        )
                                      }
                                    }}
                                  />
                                  <Label
                                    htmlFor={`schedule-${scheduleIndex}-${day}`}
                                    className="font-normal cursor-pointer"
                                  >
                                    {day.slice(0, 3)}
                                  </Label>
                                </div>
                              )
                            }}
                          />
                        ))}
                      </div>
                    </div>

                    {/* Time Periods */}
                    <div className="space-y-2">
                      <Label>Time Periods</Label>
                      <Controller
                        name={`schedules.${scheduleIndex}.periods`}
                        control={control}
                        render={({ field }) => (
                          <div className="space-y-2">
                            {field.value?.map((period, periodIndex) => (
                              <div key={periodIndex} className="flex items-center gap-2">
                                <div className="flex-1 grid grid-cols-2 gap-2">
                                  <div>
                                    <Input
                                      type="time"
                                      value={period.startTime}
                                      onChange={(e) => {
                                        const newPeriods = [...field.value]
                                        newPeriods[periodIndex] = {
                                          ...newPeriods[periodIndex],
                                          startTime: e.target.value,
                                        }
                                        field.onChange(newPeriods)
                                      }}
                                      className="h-9"
                                    />
                                  </div>
                                  <div>
                                    <Input
                                      type="time"
                                      value={period.endTime}
                                      onChange={(e) => {
                                        const newPeriods = [...field.value]
                                        newPeriods[periodIndex] = {
                                          ...newPeriods[periodIndex],
                                          endTime: e.target.value,
                                        }
                                        field.onChange(newPeriods)
                                      }}
                                      className="h-9"
                                    />
                                  </div>
                                </div>
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => {
                                    const newPeriods = field.value.filter(
                                      (_, i) => i !== periodIndex
                                    )
                                    field.onChange(newPeriods)
                                  }}
                                  disabled={field.value.length === 1}
                                  className="h-9 text-red-600 hover:text-red-700"
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </div>
                            ))}
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                field.onChange([
                                  ...field.value,
                                  { startTime: '09:00', endTime: '17:00' },
                                ])
                              }}
                              className="w-full h-9"
                            >
                              <Plus className="h-3 w-3 mr-1" />
                              Add Time Period
                            </Button>
                          </div>
                        )}
                      />
                    </div>
                  </div>
                ))
              )}

              <Button
                type="button"
                variant="outline"
                onClick={() =>
                  appendSchedule({
                    daysOfWeek: [],
                    periods: [{ startTime: '09:00', endTime: '17:00' }],
                  })
                }
                className="w-full h-9"
              >
                <Plus className="h-3 w-3 mr-1" />
                Add Schedule Block
              </Button>
            </CardContent>
          </Card>

          {/* Save Button */}
          <div className="flex items-center justify-end gap-2 pt-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => navigate('/dialer')}
              disabled={isSaving}
              className="h-9"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              size="sm"
              disabled={isSaving || !isDirty}
              className="min-w-32 h-9"
            >
              {isSaving ? 'Saving...' : 'Save Settings'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  )
}